/**
 * UsedCars — cross-dealer aggregation page at `/used-cars`. Pulls every
 * published listing from every active dealer, adds a "sold by <dealer>
 * in <city>" badge to each card, and reuses the microsite filters +
 * search hook so the UX is consistent.
 *
 * Filters supported: fuel, budget, year, km, transmission, body, city
 * (plus free-text search across make/model/etc.).
 */
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowUpRight, Search, Loader2, X } from 'lucide-react';
import PropitstopNav from '../components/PropitstopNav';
import PropitstopFooter from '../components/PropitstopFooter';
import SEO from '../components/SEO';
import { useListingFilters } from '../pages/dealers/ListingFilters';
import ListingFilters from '../pages/dealers/ListingFilters';
import { heroPhotoUrl } from '../utils/dealerPhoto';
import { useSiteImages } from '../hooks/useSiteImages';

const API = process.env.REACT_APP_BACKEND_URL;

const LUXURY_MAKES = new Set([
  'Mercedes-Benz','Mercedes Benz','Mercedes','BMW','Audi','Porsche','Jaguar','Land Rover',
  'Range Rover','Volvo','Lexus','Mini','Bentley','Rolls-Royce','Rolls Royce','Maserati',
  'Ferrari','Lamborghini','Aston Martin','Genesis','Cadillac','Infiniti','Maybach','McLaren',
]);
const isLuxury = (l) => {
  const m = (l?.ai_details?.make || '').trim();
  return LUXURY_MAKES.has(m) || Array.from(LUXURY_MAKES).some((x) => x.toLowerCase() === m.toLowerCase());
};

const heroPhoto = (photos = []) => heroPhotoUrl(photos) || '';

export default function UsedCars() {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get('q') || '';
  const [data, setData] = useState({ listings: [], cities: [], dealers_count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/public/dealers/used-cars/all`)
      .then((r) => setData(r.data))
      .catch(() => setData({ listings: [], cities: [], dealers_count: 0 }))
      .finally(() => setLoading(false));
  }, []);

  const { filters, filtered, toggle, clearAll, activeCount, query, setQuery } = useListingFilters(data.listings);
  const { imageFor } = useSiteImages();
  const bannerSrc = imageFor('used_cars_banner', null);

  // Sync initial ?q= from the URL into the search box just once
  useEffect(() => {
    if (initialQ && !query) setQuery(initialQ);
  }, [initialQ]);  // intentionally omit `query` — one-time sync

  // Reflect current query back into the URL for shareable links
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (query) next.set('q', query);
    else next.delete('q');
    setParams(next, { replace: true });
  }, [query, params, setParams]);

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="used-cars-page">
      <SEO
        title="Used Cars in Bangalore — Verified Pre-Owned Cars from Trusted Dealers | Propitstop"
        description="Browse verified used cars in Bangalore. Find second-hand cars from trusted dealers with transparent pricing, inspection reports and easy finance. Cars from top dealerships across the city — all in one place."
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/used-cars` : ''}
      />
      <PropitstopNav />

      {/* Optional admin-configurable banner (shows only if set via /admin) */}
      {bannerSrc && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-testid="used-cars-banner">
          <img
            src={bannerSrc}
            alt=""
            decoding="async"
            className="w-full max-h-64 object-contain bg-white rounded-2xl border border-zinc-200"
          />
        </section>
      )}

      {/* Header strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-blue-600 font-bold">Buy Used Cars</div>
            <h1 className="mt-1 text-3xl md:text-4xl font-extrabold tracking-tight">
              {loading ? 'Loading inventory…' : `${data.listings.length} cars from ${data.dealers_count} dealers`}
            </h1>
          </div>
        </div>
      </section>

      {/* Filters + Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        {loading ? (
          <div className="py-24 flex justify-center text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[220px,minmax(0,1fr)] gap-6 lg:gap-8">
            <ListingFilters
              filters={filters}
              toggle={toggle}
              clearAll={clearAll}
              activeCount={activeCount}
              total={data.listings.length}
              filteredCount={filtered.length}
              query={query}
              setQuery={setQuery}
            />
            <div>
              {filtered.length === 0 ? (
                <div className="p-16 text-center bg-white border border-zinc-200 rounded-sm" data-testid="used-cars-empty">
                  <p className="font-bold text-lg text-zinc-700">No cars match your filters.</p>
                  {activeCount > 0 && (
                    <button
                      onClick={clearAll}
                      className="mt-3 text-xs uppercase tracking-[0.2em] font-bold text-blue-600 hover:underline"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-6">
                  {filtered.map((l) => <UsedCarCard key={l.id} l={l} />)}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      <PropitstopFooter />
    </div>
  );
}

function UsedCarCard({ l }) {
  const hero = heroPhoto(l.photos);
  const luxury = isLuxury(l);
  const dealerSlug = l.dealer?.slug;
  const to = dealerSlug ? `/dealers/${dealerSlug}/cars/${l.slug || l.id}` : '#';
  const specBits = [
    l.year,
    l.fuel_type,
    l.ai_details?.transmission,
    l.km_driven ? `${l.km_driven.toLocaleString('en-IN')} km` : null,
  ].filter(Boolean).join(' · ');

  // Mark that the visitor arrived at the car detail page via the umbrella
  // Propitstop site — the detail page reads this to hide any "back to
  // dealer microsite" links so the customer stays inside the aggregator.
  const stampFromPropitstop = () => {
    try { sessionStorage.setItem('propitstop_ctx', '1'); } catch { /* private mode */ }
  };

  return (
    <Link
      to={to}
      onClick={stampFromPropitstop}
      data-testid={`used-car-${l.id}`}
      className={`group block bg-white border border-zinc-200 hover:border-blue-600 ${luxury ? 'ring-1 ring-[#D4AF37]/40 hover:ring-[#D4AF37]/70' : ''} rounded-sm overflow-hidden transition-colors`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
        {hero && (
          <img
            src={hero}
            alt={`${l.year || ''} ${l.ai_details?.make || ''} ${l.ai_details?.model || ''}`.trim()}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          />
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
        {luxury && (
          <span className="luxury-badge absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.24em] text-[#3A2A05] font-bold">
            <span className="luxury-badge__spark">◆</span> Luxury
          </span>
        )}
      </div>
      <div className="p-5">
        <h3 className="text-xl font-bold tracking-tight leading-tight">{l.ai_details?.make} {l.ai_details?.model}</h3>
        <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-zinc-500">{specBits}</p>

        {l.dealer?.name && (
          <div className="mt-3 flex items-center gap-1.5 text-xs text-zinc-600">
            <span className="inline-block w-1 h-1 rounded-full bg-blue-600" />
            {l.dealer.name}
            {l.dealer.city && <span className="text-zinc-400"> · {l.dealer.city}</span>}
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-zinc-100 flex items-end justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold">Price</div>
            <div className="text-2xl font-extrabold tracking-tight leading-none">₹{(l.price || 0).toLocaleString('en-IN')}</div>
          </div>
          <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.18em] text-blue-600">
            View <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
